import type {
  AiInsightDto,
  AlprRecognizeInput,
  AuthTokens,
  AuthUser,
  BarrierDto,
  CloseSessionInput,
  CreateBarrierInput,
  CreateCameraInput,
  CreateCustomerInput,
  CameraDto,
  CreateEmployeeInput,
  CreateIncidentInput,
  CreateMaintenanceInput,
  CreateParkingInput,
  CreatePlatformAdminInput,
  CreateReservationInput,
  CreateSensorInput,
  CreateSpotInput,
  CreateSubscriptionInput,
  CreateSurveillanceCameraInput,
  CreateTariffInput,
  CreateTenantInput,
  CreateVehicleInput,
  CustomerDto,
  EmployeeDto,
  FeeQuoteDto,
  FloorDto,
  HealthStatus,
  IncidentDto,
  LoginResponse,
  MaintenanceTaskDto,
  NotificationLogDto,
  OccupancySummaryDto,
  OpenAiSettingsDto,
  OpenSessionInput,
  ParkingDto,
  PlateReadDto,
  PlateRecognitionDto,
  PlatformAdminDto,
  PlatformDashboardDto,
  PlatformLoginResponse,
  PlatformSettingsDto,
  PushDeviceDto,
  PushSettingsDto,
  PushSubscribeInput,
  QuoteSessionInput,
  RecognizeMatriculaInput,
  ReportExportResult,
  ReservationDto,
  RevenueSummaryDto,
  RoleDto,
  SensorDto,
  SessionDto,
  SpotDetailDto,
  StripeIntentDto,
  StripeIntentInput,
  SubscriptionDto,
  SurveillanceCameraDto,
  TariffDto,
  TenantAdminSummary,
  TenantDto,
  TenantSettingsDto,
  UpdateBarrierInput,
  UpdateCameraInput,
  UpdateCustomerInput,
  UpdateEmployeeInput,
  UpdateIncidentInput,
  UpdateMaintenanceInput,
  UpdateOpenAiSettingsInput,
  UpdateParkingInput,
  UpdatePlatformSettingsInput,
  UpdatePushSettingsInput,
  UpdateReservationInput,
  UpdateSensorInput,
  UpdateSpotInput,
  UpdateSubscriptionInput,
  UpdateSurveillanceCameraInput,
  UpdateTariffInput,
  UpdateTenantInput,
  UpdateTenantSettingsInput,
  UpdateVehicleInput,
  UpdateVerifactuSettingsInput,
  UserDto,
  VehicleDto,
  VerifactuSettingsDto,
  ZoneDto,
} from '@parking/shared';
import { useAuthStore } from '../store/authStore';
import { usePlatformAuthStore } from '../store/platformAuthStore';
import { nativeFetch, refreshTenantSession } from './session';

async function parseJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error?.message ?? `HTTP ${res.status}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

function authHeaders(extra?: HeadersInit): HeadersInit {
  const token = useAuthStore.getState().accessToken;
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
}

function platformHeaders(extra?: HeadersInit): HeadersInit {
  const token = usePlatformAuthStore.getState().accessToken;
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
}

/**
 * Module-local fetch: on tenant 401, refresh once and retry.
 * Shadows global fetch for all calls in this file.
 */
async function fetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const res = await nativeFetch(input, init);
  if (res.status !== 401) return res;

  const url =
    typeof input === 'string'
      ? input
      : input instanceof URL
        ? input.href
        : input.url;

  if (
    url.includes('/auth/refresh') ||
    url.includes('/auth/login') ||
    url.includes('/auth/logout') ||
    url.includes('/auth/totp') ||
    url.includes('/platform/auth/')
  ) {
    return res;
  }

  const sentAuth = new Headers(init?.headers).get('Authorization');
  const tenantToken = useAuthStore.getState().accessToken;
  if (!tenantToken || sentAuth !== `Bearer ${tenantToken}`) {
    return res;
  }

  const refreshed = await refreshTenantSession();
  if (!refreshed) return res;

  const headers = new Headers(init?.headers);
  const next = useAuthStore.getState().accessToken;
  if (next) headers.set('Authorization', `Bearer ${next}`);
  return nativeFetch(input, { ...init, headers });
}

export const api = {
  async healthz(): Promise<HealthStatus> {
    const res = await fetch('/healthz');
    return parseJson<HealthStatus>(res);
  },

  async readyz(): Promise<HealthStatus> {
    const res = await fetch('/readyz');
    return parseJson<HealthStatus>(res);
  },

  async getTenantMe(tenantId: string): Promise<{ data: TenantDto }> {
    const res = await fetch('/api/v1/tenants/me', {
      headers: { 'X-Tenant-Id': tenantId },
    });
    return parseJson(res);
  },

  async login(input: {
    tenantSlug: string;
    email: string;
    password: string;
    totpCode?: string;
  }): Promise<{ data: LoginResponse }> {
    const res = await fetch('/api/v1/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },

  async verifyTotp(input: {
    preAuthToken: string;
    code: string;
  }): Promise<{ data: LoginResponse }> {
    const res = await fetch('/api/v1/auth/totp/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },

  async refresh(refreshToken: string): Promise<{ data: AuthTokens }> {
    const res = await fetch('/api/v1/auth/refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    return parseJson(res);
  },

  async logout(refreshToken?: string): Promise<void> {
    await fetch('/api/v1/auth/logout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
  },

  async me(): Promise<{
    data: Pick<AuthUser, 'id' | 'tenantId' | 'email' | 'roles' | 'permissions'>;
  }> {
    const res = await fetch('/api/v1/auth/me', { headers: authHeaders() });
    return parseJson(res);
  },

  async listUsers(): Promise<{ data: UserDto[] }> {
    const res = await fetch('/api/v1/users', { headers: authHeaders() });
    return parseJson(res);
  },

  async listRoles(): Promise<{ data: RoleDto[] }> {
    const res = await fetch('/api/v1/roles', { headers: authHeaders() });
    return parseJson(res);
  },

  async listParkings(): Promise<{ data: ParkingDto[] }> {
    const res = await fetch('/api/v1/parkings', { headers: authHeaders() });
    return parseJson(res);
  },

  async createParking(input: CreateParkingInput): Promise<{ data: ParkingDto }> {
    const res = await fetch('/api/v1/parkings', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },

  async updateParking(id: string, input: UpdateParkingInput): Promise<{ data: ParkingDto }> {
    const res = await fetch(`/api/v1/parkings/${id}`, {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },

  async deleteParking(id: string): Promise<void> {
    const res = await fetch(`/api/v1/parkings/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    return parseJson(res);
  },

  async listFloors(parkingId: string): Promise<{ data: FloorDto[] }> {
    const res = await fetch(`/api/v1/parkings/${parkingId}/floors`, {
      headers: authHeaders(),
    });
    return parseJson(res);
  },

  async createFloor(input: {
    parkingId: string;
    code: string;
    name: string;
    level?: number;
  }): Promise<{ data: FloorDto }> {
    const res = await fetch('/api/v1/floors', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },

  async listZones(parkingId: string): Promise<{ data: ZoneDto[] }> {
    const res = await fetch(`/api/v1/parkings/${parkingId}/zones`, {
      headers: authHeaders(),
    });
    return parseJson(res);
  },

  async createZone(input: {
    parkingId: string;
    floorId?: string | null;
    code: string;
    name: string;
    color?: string;
  }): Promise<{ data: ZoneDto }> {
    const res = await fetch('/api/v1/zones', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },

  async listSpots(parkingId?: string): Promise<{ data: SpotDetailDto[] }> {
    const q = parkingId ? `?parkingId=${encodeURIComponent(parkingId)}` : '';
    const res = await fetch(`/api/v1/spots${q}`, { headers: authHeaders() });
    return parseJson(res);
  },

  async createSpot(input: CreateSpotInput): Promise<{ data: SpotDetailDto }> {
    const res = await fetch('/api/v1/spots', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },

  async updateSpot(id: string, input: UpdateSpotInput): Promise<{ data: SpotDetailDto }> {
    const res = await fetch(`/api/v1/spots/${id}`, {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },

  async deleteSpot(id: string): Promise<void> {
    const res = await fetch(`/api/v1/spots/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    return parseJson(res);
  },

  async listOpenSessions(): Promise<{ data: SessionDto[] }> {
    const res = await fetch('/api/v1/sessions/open', { headers: authHeaders() });
    return parseJson(res);
  },

  async listInvoices(status?: string): Promise<{ data: import('@parking/shared').InvoiceDto[] }> {
    const q = status ? `?status=${encodeURIComponent(status)}` : '';
    const res = await fetch(`/api/v1/invoices${q}`, { headers: authHeaders() });
    return parseJson(res);
  },

  async getInvoice(id: string): Promise<{ data: import('@parking/shared').InvoiceDto }> {
    const res = await fetch(`/api/v1/invoices/${id}`, { headers: authHeaders() });
    return parseJson(res);
  },

  async listInvoiceSeries(): Promise<{
    data: import('@parking/shared').InvoiceSeriesDto[];
  }> {
    const res = await fetch('/api/v1/invoice-series', { headers: authHeaders() });
    return parseJson(res);
  },

  async posCharge(input: import('@parking/shared').PosChargeInput): Promise<{
    data: import('@parking/shared').PosChargeResult;
  }> {
    const res = await fetch('/api/v1/pos/charge', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },

  async issueInvoiceFromSession(
    input: import('@parking/shared').IssueInvoiceInput,
  ): Promise<{ data: import('@parking/shared').InvoiceDto }> {
    const res = await fetch('/api/v1/invoices/from-session', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },

  async voidInvoice(id: string): Promise<{ data: import('@parking/shared').InvoiceDto }> {
    const res = await fetch(`/api/v1/invoices/${id}/void`, {
      method: 'POST',
      headers: authHeaders(),
    });
    return parseJson(res);
  },

  async listSessions(params?: {
    status?: string;
    parkingId?: string;
  }): Promise<{ data: SessionDto[] }> {
    const q = new URLSearchParams();
    if (params?.status) q.set('status', params.status);
    if (params?.parkingId) q.set('parkingId', params.parkingId);
    const qs = q.toString();
    const res = await fetch(`/api/v1/sessions${qs ? `?${qs}` : ''}`, {
      headers: authHeaders(),
    });
    return parseJson(res);
  },

  async openSession(input: OpenSessionInput): Promise<{ data: SessionDto }> {
    const res = await fetch('/api/v1/sessions/open', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },

  async quoteSession(input: QuoteSessionInput): Promise<{ data: FeeQuoteDto }> {
    const res = await fetch('/api/v1/sessions/quote', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },

  async closeSession(id: string, input: CloseSessionInput = {}): Promise<{ data: SessionDto }> {
    const res = await fetch(`/api/v1/sessions/${id}/close`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },

  async cancelSession(id: string): Promise<{ data: SessionDto }> {
    const res = await fetch(`/api/v1/sessions/${id}/cancel`, {
      method: 'POST',
      headers: authHeaders(),
    });
    return parseJson(res);
  },

  async listTariffs(parkingId?: string): Promise<{ data: TariffDto[] }> {
    const q = parkingId ? `?parkingId=${encodeURIComponent(parkingId)}` : '';
    const res = await fetch(`/api/v1/tariffs${q}`, { headers: authHeaders() });
    return parseJson(res);
  },

  async createTariff(input: CreateTariffInput): Promise<{ data: TariffDto }> {
    const res = await fetch('/api/v1/tariffs', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },

  async updateTariff(id: string, input: UpdateTariffInput): Promise<{ data: TariffDto }> {
    const res = await fetch(`/api/v1/tariffs/${id}`, {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },

  async deleteTariff(id: string): Promise<void> {
    const res = await fetch(`/api/v1/tariffs/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    return parseJson(res);
  },

  async listCustomers(): Promise<{ data: CustomerDto[] }> {
    const res = await fetch('/api/v1/customers', { headers: authHeaders() });
    return parseJson(res);
  },

  async createCustomer(input: CreateCustomerInput): Promise<{ data: CustomerDto }> {
    const res = await fetch('/api/v1/customers', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },

  async updateCustomer(id: string, input: UpdateCustomerInput): Promise<{ data: CustomerDto }> {
    const res = await fetch(`/api/v1/customers/${id}`, {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },

  async deleteCustomer(id: string): Promise<void> {
    const res = await fetch(`/api/v1/customers/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    return parseJson(res);
  },

  async linkVehicle(
    customerId: string,
    input: { vehicleId: string; isPrimary?: boolean },
  ): Promise<{ data: VehicleDto }> {
    const res = await fetch(`/api/v1/customers/${customerId}/vehicles`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },

  async unlinkVehicle(customerId: string, vehicleId: string): Promise<void> {
    const res = await fetch(`/api/v1/customers/${customerId}/vehicles/${vehicleId}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    return parseJson(res);
  },

  async listVehicles(customerId?: string): Promise<{ data: VehicleDto[] }> {
    const q = customerId ? `?customerId=${encodeURIComponent(customerId)}` : '';
    const res = await fetch(`/api/v1/vehicles${q}`, { headers: authHeaders() });
    return parseJson(res);
  },

  async createVehicle(input: CreateVehicleInput): Promise<{ data: VehicleDto }> {
    const res = await fetch('/api/v1/vehicles', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },

  async updateVehicle(id: string, input: UpdateVehicleInput): Promise<{ data: VehicleDto }> {
    const res = await fetch(`/api/v1/vehicles/${id}`, {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },

  async deleteVehicle(id: string): Promise<void> {
    const res = await fetch(`/api/v1/vehicles/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    return parseJson(res);
  },

  async listSubscriptions(customerId?: string): Promise<{ data: SubscriptionDto[] }> {
    const q = customerId ? `?customerId=${encodeURIComponent(customerId)}` : '';
    const res = await fetch(`/api/v1/subscriptions${q}`, { headers: authHeaders() });
    return parseJson(res);
  },

  async createSubscription(input: CreateSubscriptionInput): Promise<{ data: SubscriptionDto }> {
    const res = await fetch('/api/v1/subscriptions', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },

  async updateSubscription(
    id: string,
    input: UpdateSubscriptionInput,
  ): Promise<{ data: SubscriptionDto }> {
    const res = await fetch(`/api/v1/subscriptions/${id}`, {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },

  async deleteSubscription(id: string): Promise<void> {
    const res = await fetch(`/api/v1/subscriptions/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    return parseJson(res);
  },

  /* ─── Tenant settings ───────────────────────────────────────────────── */

  async getCompanySettings(): Promise<{ data: TenantSettingsDto }> {
    const res = await fetch('/api/v1/settings/company', { headers: authHeaders() });
    return parseJson(res);
  },

  async updateCompanySettings(
    input: UpdateTenantSettingsInput,
  ): Promise<{ data: TenantSettingsDto }> {
    const res = await fetch('/api/v1/settings/company', {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },

  async getVerifactuSettings(): Promise<{ data: VerifactuSettingsDto }> {
    const res = await fetch('/api/v1/settings/verifactu', { headers: authHeaders() });
    return parseJson(res);
  },

  async updateVerifactuSettings(
    input: UpdateVerifactuSettingsInput,
  ): Promise<{ data: VerifactuSettingsDto }> {
    const res = await fetch('/api/v1/settings/verifactu', {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },

  async validateVerifactuSettings(): Promise<{ data: VerifactuSettingsDto }> {
    const res = await fetch('/api/v1/settings/verifactu/validate', {
      method: 'POST',
      headers: authHeaders(),
    });
    return parseJson(res);
  },

  async getPushSettings(): Promise<{ data: PushSettingsDto }> {
    const res = await fetch('/api/v1/settings/push', { headers: authHeaders() });
    return parseJson(res);
  },

  async updatePushSettings(input: UpdatePushSettingsInput): Promise<{ data: PushSettingsDto }> {
    const res = await fetch('/api/v1/settings/push', {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },

  async getOpenAiSettings(): Promise<{ data: OpenAiSettingsDto }> {
    const res = await fetch('/api/v1/settings/openai', { headers: authHeaders() });
    return parseJson(res);
  },

  async updateOpenAiSettings(
    input: UpdateOpenAiSettingsInput,
  ): Promise<{ data: OpenAiSettingsDto }> {
    const res = await fetch('/api/v1/settings/openai', {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },

  async listPushDevices(): Promise<{ data: PushDeviceDto[] }> {
    const res = await fetch('/api/v1/settings/push/devices', { headers: authHeaders() });
    return parseJson(res);
  },

  async subscribePush(input: PushSubscribeInput): Promise<{ data: PushDeviceDto }> {
    const res = await fetch('/api/v1/settings/push/subscribe', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },

  async unsubscribePush(id: string): Promise<void> {
    const res = await fetch(`/api/v1/settings/push/devices/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    return parseJson(res);
  },

  async testPush(input?: { title?: string; body?: string }): Promise<{
    data: { sent: number; failed: number };
  }> {
    const res = await fetch('/api/v1/settings/push/test', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(input ?? {}),
    });
    return parseJson(res);
  },

  async listNotifications(): Promise<{ data: NotificationLogDto[] }> {
    const res = await fetch('/api/v1/settings/notifications', { headers: authHeaders() });
    return parseJson(res);
  },

  /* ─── Platform ──────────────────────────────────────────────────────── */

  async platformLogin(input: {
    email: string;
    password: string;
  }): Promise<{ data: PlatformLoginResponse }> {
    const res = await fetch('/api/v1/platform/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },

  async platformMe(): Promise<{ data: PlatformAdminDto }> {
    const res = await fetch('/api/v1/platform/me', { headers: platformHeaders() });
    return parseJson(res);
  },

  async platformDashboard(): Promise<{ data: PlatformDashboardDto }> {
    const res = await fetch('/api/v1/platform/dashboard', { headers: platformHeaders() });
    return parseJson(res);
  },

  async platformListTenants(): Promise<{ data: TenantAdminSummary[] }> {
    const res = await fetch('/api/v1/platform/tenants', { headers: platformHeaders() });
    return parseJson(res);
  },

  async platformCreateTenant(
    input: CreateTenantInput,
  ): Promise<{ data: { tenant: TenantDto; adminEmail?: string } }> {
    const res = await fetch('/api/v1/platform/tenants', {
      method: 'POST',
      headers: platformHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },

  async platformUpdateTenant(
    id: string,
    input: UpdateTenantInput,
  ): Promise<{ data: TenantAdminSummary }> {
    const res = await fetch(`/api/v1/platform/tenants/${id}`, {
      method: 'PATCH',
      headers: platformHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },

  async platformGetSettings(): Promise<{ data: PlatformSettingsDto }> {
    const res = await fetch('/api/v1/platform/settings', { headers: platformHeaders() });
    return parseJson(res);
  },

  async platformUpdateSettings(
    input: UpdatePlatformSettingsInput,
  ): Promise<{ data: PlatformSettingsDto }> {
    const res = await fetch('/api/v1/platform/settings', {
      method: 'PATCH',
      headers: platformHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },

  async platformListAdmins(): Promise<{ data: PlatformAdminDto[] }> {
    const res = await fetch('/api/v1/platform/admins', { headers: platformHeaders() });
    return parseJson(res);
  },

  async platformCreateAdmin(
    input: CreatePlatformAdminInput,
  ): Promise<{ data: PlatformAdminDto }> {
    const res = await fetch('/api/v1/platform/admins', {
      method: 'POST',
      headers: platformHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },

  /* ── Phase 8 Ops ── */
  async listReservations(status?: string): Promise<{ data: ReservationDto[] }> {
    const q = status ? `?status=${encodeURIComponent(status)}` : '';
    const res = await fetch(`/api/v1/reservations${q}`, { headers: authHeaders() });
    return parseJson(res);
  },
  async createReservation(input: CreateReservationInput): Promise<{ data: ReservationDto }> {
    const res = await fetch('/api/v1/reservations', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },
  async updateReservation(
    id: string,
    input: UpdateReservationInput,
  ): Promise<{ data: ReservationDto }> {
    const res = await fetch(`/api/v1/reservations/${id}`, {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },
  async deleteReservation(id: string): Promise<void> {
    const res = await fetch(`/api/v1/reservations/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    return parseJson(res);
  },
  async confirmReservation(id: string): Promise<{ data: ReservationDto }> {
    const res = await fetch(`/api/v1/reservations/${id}/confirm`, {
      method: 'POST',
      headers: authHeaders(),
    });
    return parseJson(res);
  },
  async cancelReservation(id: string): Promise<{ data: ReservationDto }> {
    const res = await fetch(`/api/v1/reservations/${id}/cancel`, {
      method: 'POST',
      headers: authHeaders(),
    });
    return parseJson(res);
  },
  async checkInReservation(id: string): Promise<{ data: ReservationDto }> {
    const res = await fetch(`/api/v1/reservations/${id}/check-in`, {
      method: 'POST',
      headers: authHeaders(),
    });
    return parseJson(res);
  },

  async listIncidents(status?: string): Promise<{ data: IncidentDto[] }> {
    const q = status ? `?status=${encodeURIComponent(status)}` : '';
    const res = await fetch(`/api/v1/incidents${q}`, { headers: authHeaders() });
    return parseJson(res);
  },
  async createIncident(input: CreateIncidentInput): Promise<{ data: IncidentDto }> {
    const res = await fetch('/api/v1/incidents', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },
  async updateIncident(id: string, input: UpdateIncidentInput): Promise<{ data: IncidentDto }> {
    const res = await fetch(`/api/v1/incidents/${id}`, {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },
  async resolveIncident(id: string): Promise<{ data: IncidentDto }> {
    const res = await fetch(`/api/v1/incidents/${id}/resolve`, {
      method: 'POST',
      headers: authHeaders(),
    });
    return parseJson(res);
  },

  async listMaintenance(status?: string): Promise<{ data: MaintenanceTaskDto[] }> {
    const q = status ? `?status=${encodeURIComponent(status)}` : '';
    const res = await fetch(`/api/v1/maintenance${q}`, { headers: authHeaders() });
    return parseJson(res);
  },
  async createMaintenance(input: CreateMaintenanceInput): Promise<{ data: MaintenanceTaskDto }> {
    const res = await fetch('/api/v1/maintenance', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },
  async updateMaintenance(
    id: string,
    input: UpdateMaintenanceInput,
  ): Promise<{ data: MaintenanceTaskDto }> {
    const res = await fetch(`/api/v1/maintenance/${id}`, {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },
  async completeMaintenance(id: string): Promise<{ data: MaintenanceTaskDto }> {
    const res = await fetch(`/api/v1/maintenance/${id}/complete`, {
      method: 'POST',
      headers: authHeaders(),
    });
    return parseJson(res);
  },

  async listEmployees(): Promise<{ data: EmployeeDto[] }> {
    const res = await fetch('/api/v1/employees', { headers: authHeaders() });
    return parseJson(res);
  },
  async createEmployee(input: CreateEmployeeInput): Promise<{ data: EmployeeDto }> {
    const res = await fetch('/api/v1/employees', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },
  async updateEmployee(id: string, input: UpdateEmployeeInput): Promise<{ data: EmployeeDto }> {
    const res = await fetch(`/api/v1/employees/${id}`, {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },
  async deleteEmployee(id: string): Promise<void> {
    const res = await fetch(`/api/v1/employees/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    return parseJson(res);
  },

  /* ── Phase 9 Hardware ── */
  async listBarriers(): Promise<{ data: BarrierDto[] }> {
    const res = await fetch('/api/v1/barriers', { headers: authHeaders() });
    return parseJson(res);
  },
  async createBarrier(input: CreateBarrierInput): Promise<{ data: BarrierDto }> {
    const res = await fetch('/api/v1/barriers', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },
  async updateBarrier(id: string, input: UpdateBarrierInput): Promise<{ data: BarrierDto }> {
    const res = await fetch(`/api/v1/barriers/${id}`, {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },
  async deleteBarrier(id: string): Promise<void> {
    const res = await fetch(`/api/v1/barriers/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    return parseJson(res);
  },
  async openBarrier(id: string): Promise<{ data: BarrierDto }> {
    const res = await fetch(`/api/v1/barriers/${id}/open`, {
      method: 'POST',
      headers: authHeaders(),
    });
    return parseJson(res);
  },
  async closeBarrier(id: string): Promise<{ data: BarrierDto }> {
    const res = await fetch(`/api/v1/barriers/${id}/close`, {
      method: 'POST',
      headers: authHeaders(),
    });
    return parseJson(res);
  },
  async barrierStatus(id: string): Promise<{ data: BarrierDto }> {
    const res = await fetch(`/api/v1/barriers/${id}/status`, { headers: authHeaders() });
    return parseJson(res);
  },
  async listSensors(): Promise<{ data: SensorDto[] }> {
    const res = await fetch('/api/v1/sensors', { headers: authHeaders() });
    return parseJson(res);
  },
  async createSensor(input: CreateSensorInput): Promise<{ data: SensorDto }> {
    const res = await fetch('/api/v1/sensors', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },
  async updateSensor(id: string, input: UpdateSensorInput): Promise<{ data: SensorDto }> {
    const res = await fetch(`/api/v1/sensors/${id}`, {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },
  async deleteSensor(id: string): Promise<void> {
    const res = await fetch(`/api/v1/sensors/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    return parseJson(res);
  },
  async listCameras(): Promise<{ data: CameraDto[] }> {
    const res = await fetch('/api/v1/cameras', { headers: authHeaders() });
    return parseJson(res);
  },
  async createCamera(input: CreateCameraInput): Promise<{ data: CameraDto }> {
    const res = await fetch('/api/v1/cameras', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },
  async updateCamera(id: string, input: UpdateCameraInput): Promise<{ data: CameraDto }> {
    const res = await fetch(`/api/v1/cameras/${id}`, {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },
  async deleteCamera(id: string): Promise<void> {
    const res = await fetch(`/api/v1/cameras/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    if (!res.ok) await parseJson(res);
  },
  async listSurveillanceCameras(activeOnly = false): Promise<{ data: SurveillanceCameraDto[] }> {
    const q = activeOnly ? '?active=1' : '';
    const res = await fetch(`/api/v1/surveillance-cameras${q}`, { headers: authHeaders() });
    return parseJson(res);
  },
  async createSurveillanceCamera(
    input: CreateSurveillanceCameraInput,
  ): Promise<{ data: SurveillanceCameraDto }> {
    const res = await fetch('/api/v1/surveillance-cameras', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },
  async updateSurveillanceCamera(
    id: string,
    input: UpdateSurveillanceCameraInput,
  ): Promise<{ data: SurveillanceCameraDto }> {
    const res = await fetch(`/api/v1/surveillance-cameras/${id}`, {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },
  async deleteSurveillanceCamera(id: string): Promise<void> {
    const res = await fetch(`/api/v1/surveillance-cameras/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    if (!res.ok) await parseJson(res);
  },
  async recognizeAlpr(input: AlprRecognizeInput): Promise<{ data: PlateReadDto }> {
    const res = await fetch('/api/v1/alpr/recognize', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },
  async listPlateReads(): Promise<{ data: PlateReadDto[] }> {
    const res = await fetch('/api/v1/alpr/reads', { headers: authHeaders() });
    return parseJson(res);
  },
  async listMatriculas(): Promise<{ data: PlateRecognitionDto[] }> {
    const res = await fetch('/api/v1/matriculas', { headers: authHeaders() });
    return parseJson(res);
  },
  async recognizeMatricula(
    input: RecognizeMatriculaInput,
  ): Promise<{ data: PlateRecognitionDto }> {
    const res = await fetch('/api/v1/matriculas/recognize', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },

  /* ── Phase 10 Reports / AI ── */
  async occupancyReport(parkingId?: string): Promise<{ data: OccupancySummaryDto }> {
    const q = parkingId ? `?parkingId=${encodeURIComponent(parkingId)}` : '';
    const res = await fetch(`/api/v1/reports/occupancy${q}`, { headers: authHeaders() });
    return parseJson(res);
  },
  async revenueReport(from?: string, to?: string): Promise<{ data: RevenueSummaryDto }> {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const q = params.toString() ? `?${params}` : '';
    const res = await fetch(`/api/v1/reports/revenue${q}`, { headers: authHeaders() });
    return parseJson(res);
  },
  async exportSessionsReport(
    format: 'csv' | 'xlsx' | 'pdf' = 'csv',
  ): Promise<{ data: ReportExportResult }> {
    const res = await fetch(`/api/v1/reports/sessions/export?format=${format}`, {
      headers: authHeaders(),
    });
    return parseJson(res);
  },
  async listAiInsights(): Promise<{ data: AiInsightDto[] }> {
    const res = await fetch('/api/v1/ai/insights', { headers: authHeaders() });
    return parseJson(res);
  },
  async generateAiInsight(input?: {
    kind?: string;
    parkingId?: string | null;
  }): Promise<{ data: AiInsightDto }> {
    const res = await fetch('/api/v1/ai/insights/generate', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(input ?? {}),
    });
    return parseJson(res);
  },
  async createStripeIntent(input: StripeIntentInput): Promise<{ data: StripeIntentDto }> {
    const res = await fetch('/api/v1/payments/stripe/intent', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(input),
    });
    return parseJson(res);
  },
};
